import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import {
  CircularProgress,
  Dialog,
  DialogContent,
  InputAdornment,
  List,
  TextField,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import IconButton from "@mui/material/IconButton";
import React, { useEffect, useState } from "react";

import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import Suspense from "@/components/molecules/Suspense";
import useGetContacts from "@/hooks/requests/contact/use-get-contacts";
import useFilters from "@/hooks/use-filters";
import IContact from "@/interfaces/contact.interface";
import { ChatContactListMessages } from "@/messages/chat/contact-list.messages";
import { FilterType, IFilter } from "@/types/filter.types";

import ListItem from "./listItem";
import { ACTIVE_CONTACT_FILTER, initialFilterValues, pageNumber, pageSize } from "../../constants";

type ContactsModalProps = {
  onClose: () => void;
  onSelect: (contact: IContact) => void;
  messages: ChatContactListMessages;
};

const ContactsModal: React.FC<ContactsModalProps> = ({ onClose, onSelect, messages }) => {
  const [searchTerm, setSearchTerm] = useState("");

  const { searchChatContactPlaceholder, dialogTitle } = messages;

  const { filters, changeFilters } = useFilters<IContact>({
    initialValue: initialFilterValues,
  });

  const { data, isLoading } = useGetContacts({ filters, pageNumber, pageSize });

  useEffect(() => {
    if (searchTerm) {
      const searchFilter: IFilter<IContact> = {
        property: "firstName",
        filterType: FilterType.Contains,
        value: searchTerm,
      };
      changeFilters([searchFilter, ACTIVE_CONTACT_FILTER]);
    } else {
      changeFilters([ACTIVE_CONTACT_FILTER]);
    }
  }, [searchTerm, changeFilters]);

  const sortedContacts = data?.items
    ?.filter((contact) => contact.active !== false)
    .sort((a, b) => a.firstName.localeCompare(b.firstName));

  let lastInitial = "";

  return (
    <Dialog open={true} onClose={onClose} fullWidth>
      <DialogContent>
        <IconButton
          onClick={onClose}
          sx={(theme) => ({
            position: "absolute",
            top: theme.functions.pxToRem(12),
            right: theme.functions.pxToRem(12),
          })}
        >
          <CloseIcon />
        </IconButton>

        <Grid>
          <MDTypography variant="h5" fontWeight="bold">
            {dialogTitle}
          </MDTypography>

          <MDBox my={1}>
            <TextField
              placeholder={searchChatContactPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              fullWidth
              slotProps={{
                inputLabel: { shrink: true },
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                },
              }}
            />
          </MDBox>
        </Grid>

        <Suspense isSuspended={isLoading} fallback={<CircularProgress size={60} />}>
          <List>
            {sortedContacts?.map((contact) => {
              const currentInitial = contact.firstName.charAt(0).toUpperCase();
              const shouldShowInitial = currentInitial !== lastInitial;
              lastInitial = currentInitial;

              return (
                <React.Fragment key={contact.id}>
                  {shouldShowInitial && (
                    <MDTypography sx={{ margin: "25px" }} variant="h6" fontWeight={"bold"}>
                      {currentInitial}
                    </MDTypography>
                  )}
                  <ListItem contact={contact} onSelect={onSelect} messages={messages} />
                </React.Fragment>
              );
            })}
          </List>
        </Suspense>
      </DialogContent>
    </Dialog>
  );
};

export default ContactsModal;
